<script setup lang="ts">
import type { ScaleLinear } from 'd3';
import type { LineChartOptions } from '../index.vue';
import { useElementBounding } from '@vueuse/core';

// ================================================ Composables
const { formatNumberToIsoNumber } = useNumbers();

// ================================================ Props
const rightMargin = defineModel<number>({ required: true, default: 8 });
const props = defineProps<{
  loading: boolean;
  yScale: ScaleLinear<number, number>;
  options: LineChartOptions;
  customTicks?: number[];
}>();

// ================================================ Data
const yAxisValues = ref<HTMLDivElement[]>([]);

// ================================================ Computed
const ticksToUse = computed(() => props.customTicks || props.yScale.ticks(8));
const maxTickValue = computed(() => ticksToUse.value.at(-1));

// ================================================ Methods
const updateRightMargin = (value: HTMLElement[]) => {
  if (!value.length) return 0;
  const values = value.map((value) => {
    const { width } = useElementBounding(value);
    return width.value;
  });
  rightMargin.value = Math.max(...values);
};

// ================================================ Watch
watch(
  () => yAxisValues.value,
  () => {
    updateRightMargin(yAxisValues.value);
  },
  { deep: true, immediate: true },
);
</script>
<template>
  <div
    class="relative h-[calc(100%-var(--marginTop)-var(--marginBottom))] w-[var(--marginLeft)] translate-y-[var(--marginTop)] overflow-visible"
  >
    <div
      v-for="(value, i) in ticksToUse"
      ref="yAxisValues"
      :key="i"
      :style="{ top: `${yScale(value)}%` }"
      class="absolute left-0 text-xs tabular-nums -translate-y-1/2 text-gray-400 text-right pr-2 whitespace-nowrap"
    >
      <template v-if="!loading">
        <template v-if="options?.unit && i === ticksToUse.length - 1">
          {{ options.unit }}
        </template>
        <template v-else>
          {{ formatNumberToIsoNumber(value, maxTickValue ? (maxTickValue > 10 ? 0 : 2) : 0) }}
        </template>
      </template>
    </div>
  </div>
</template>
